import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { StyledFlexStart } from "../../Components/Styled";
import LeadAccordion from "../../Components/LeadAccordion";
import TextFiledWithLabel from "../../Components/TextFieldWithLabel";
import SelectBoxWithLabel from "../../Components/SelectBoxWithLable";
import InfoIcon from "../../Components/Widgets/InfoIcon";
import {
  handleValidateCodevalue,
  MCP_CLIENT_OPTIONS,
  getSnippetByClient,
} from "../../Utility/constant";
import { createRequestData, getRequestData } from "../../Utility/apiHelper";
import { requestHideLoader, requestShowLoader } from "../../Store/Global/action";
import "../../Assets/css/mcpSettings.css";

const McpSettings = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const internationalise = useSelector(
    (state) => state?.internationalise?.currentHashMapData
  );

  const [mcpState, setMcpState] = useState({
    currentToken: "",
    tokenCopied: false,
    snippetCopied: false,
    selectedClient: "cursor",
  });

  const { currentToken, tokenCopied, snippetCopied, selectedClient } = mcpState;

  const handleUpdateState = (fields) => {
    setMcpState((prevState) => ({ ...prevState, ...fields }));
  };

  useEffect(() => {
    const fetchActivePat = async () => {
      try {
        const url = `${process.env.REACT_APP_BASE_URL}mcp/pat`;
        const response = await getRequestData(url);
        if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
          const pat = response.data[0];
          handleUpdateState({
            currentToken: pat.token || pat.rawToken || pat.tokenPrefix || "",
          });
        }
      } catch {
        // Silently ignore if no tokens exist yet
      }
    };

    fetchActivePat();
  }, []);

  const handleGenerateToken = async () => {
    try {
      dispatch(requestShowLoader());
      const url = `${process.env.REACT_APP_BASE_URL}mcp/pat`;
      const response = await createRequestData({ label: "MCP AI Token" }, url);

      if (response?.status === 200 || response?.status === 201) {
        const fullToken = response.data?.token || response.data?.rawToken;
        const wasRegenerate = Boolean(currentToken);
        if (fullToken) {
          handleUpdateState({ currentToken: fullToken });
        }
        enqueueSnackbar(
          wasRegenerate
            ? (handleValidateCodevalue(internationalise, "MCP_PAT_REGENERATED_SUCCESS") ||
                "Personal Access Token regenerated successfully. Previous token has been invalidated.")
            : (handleValidateCodevalue(internationalise, "MCP_PAT_GENERATED_SUCCESS") ||
                "Personal Access Token generated successfully"),
          { variant: "success" }
        );
      } else {
        enqueueSnackbar(
          response?.data?.message ||
            handleValidateCodevalue(internationalise, "COMMON_PLEASE_TRY_AGAIN"),
          { variant: "error" }
        );
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        handleValidateCodevalue(internationalise, "COMMON_PLEASE_TRY_AGAIN");
      enqueueSnackbar(errMsg, { variant: "error" });
    } finally {
      dispatch(requestHideLoader());
    }
  };

  const copyToClipboard = async (text, isSnippet = false) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }

      if (isSnippet) {
        handleUpdateState({ snippetCopied: true });
        setTimeout(() => handleUpdateState({ snippetCopied: false }), 2000);
      } else {
        handleUpdateState({ tokenCopied: true });
        setTimeout(() => handleUpdateState({ tokenCopied: false }), 2000);
      }
    } catch {
      // ignore
    }
  };

  const activeSnippet = getSnippetByClient(selectedClient, currentToken);

  return (
    <React.Fragment>
      <Box className="mcp-tab-wrapper">
        <Box className="hide-scrollbar globalBox mcp-global-box">
          <LeadAccordion
            defaultOpen={true}
            label={handleValidateCodevalue(internationalise, "MCP_SETUP_GUIDE_TITLE")}
          >
            <div className="mcp-steps-container">
              {/* Step 1 — Personal Access Token Generation */}
              <Box>
                <Typography variant="subtitle2" className="mcp-step-title">
                  Step 1 — Generate Personal Access Token:
                </Typography>
                <InfoIcon text="MCP_STEP1_INFO" />

                <StyledFlexStart className="gap-40">
                  <StyledFlexStart>
                    <TextFiledWithLabel
                      name="token"
                      disabled
                      className="mcp-token-input"
                      value={currentToken}
                      label={handleValidateCodevalue(internationalise, "MCP_PAT_TOKEN") || "Personal Access Token"}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Click 'Generate Token' to create your PAT"
                    />
                    {!tokenCopied ? (
                      <Tooltip title={handleValidateCodevalue(internationalise, "COMMON_COPY")}>
                        <IconButton
                          disabled={!currentToken}
                          onClick={() => copyToClipboard(currentToken)}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box className="mcp-copied-badge">
                        <CheckIcon className="mcp-copied-icon" />
                        {handleValidateCodevalue(internationalise, "COMMON_COPIED")}
                      </Box>
                    )}
                  </StyledFlexStart>

                  <Tooltip
                    title={
                      currentToken
                        ? (handleValidateCodevalue(internationalise, "MCP_REGENERATE_PAT") || "Regenerate Token")
                        : (handleValidateCodevalue(internationalise, "MCP_GENERATE_PAT") || "Generate Token")}
                  >
                    <Button
                      variant="outlined"
                      className="biztantu-sec-btn mcp-token-btn"
                      onClick={handleGenerateToken}
                    >
                      {currentToken
                        ? (handleValidateCodevalue(internationalise, "MCP_REGENERATE_PAT") || "Regenerate Token")
                        : (handleValidateCodevalue(internationalise, "MCP_GENERATE_PAT") || "Generate Token")}
                    </Button>
                  </Tooltip>
                </StyledFlexStart>
              </Box>

              {/* Step 2 — Configuration Snippet with Tool Dropdown */}
              <Box>
                <Typography variant="subtitle2" className="mcp-step-title">
                  Step 2 — Copy Configuration:
                </Typography>
                <InfoIcon text="MCP_STEP2_INFO" />

                <Paper elevation={0} className="mcp-snippet-card">
                  <Box className="mcp-snippet-header">
                    <SelectBoxWithLabel
                      name="clientType"
                      label="Client / IDE"
                      className="mcp-client-select"
                      value={selectedClient}
                      isStatic={true}
                      optionMenuItem={MCP_CLIENT_OPTIONS}
                      handleOnChange={(e) => handleUpdateState({ selectedClient: e.target.value })}
                    />

                    {!snippetCopied ? (
                      <Tooltip title={handleValidateCodevalue(internationalise, "COMMON_COPY")}>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(activeSnippet, true)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box className="mcp-copied-badge">
                        <CheckIcon className="mcp-copied-icon" />
                        {handleValidateCodevalue(internationalise, "COMMON_COPIED")}
                      </Box>
                    )}
                  </Box>

                  <Box component="pre" className="mcp-code-block jsonCodeBlock">
                    {activeSnippet}
                  </Box>
                </Paper>
              </Box>

              {/* Step 3 — Locate Config File & Paste */}
              <Box>
                <Typography variant="subtitle2" className="mcp-step-title">
                  Step 3 — Locate Config File & Paste:
                </Typography>
                <InfoIcon text="MCP_STEP3_INFO" />
                <ul className="mcp-client-list">
                  <li>
                    <strong>Cursor / VS Code:</strong> <code>.cursor/mcp.json</code> (project root) or global settings
                  </li>
                  <li>
                    <strong>Google Antigravity:</strong> <code>~/.gemini/config/mcp_config.json</code>
                  </li>
                  <li>
                    <strong>Claude Desktop:</strong> <code>claude_desktop_config.json</code>
                  </li>
                  <li>
                    <strong>ChatGPT / Codex (TOML):</strong> <code>config.toml</code>
                  </li>
                  <li>
                    <strong>Windsurf:</strong> <code>~/.codeium/windsurf/mcp_config.json</code>
                  </li>
                </ul>
              </Box>

              {/* Step 4 — Restart or Reload */}
              <Box>
                <Typography variant="subtitle2" className="mcp-step-title">
                  Step 4 — Restart or Reload Client:
                </Typography>
                <InfoIcon text="MCP_STEP4_INFO" />
              </Box>
            </div>
          </LeadAccordion>
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default McpSettings;
